import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Table, Button, Form, Input, Select, Space, message, Tag, Row, Col,
    Card, Divider, Typography, DatePicker, Modal, InputNumber, Steps,
    Alert, Tooltip, Popconfirm
} from 'antd';
import {
    PlusOutlined, SyncOutlined, ClearOutlined,
    ArrowRightOutlined, SaveOutlined, SearchOutlined,
    EyeOutlined, FileExcelOutlined, FilePdfOutlined,
    DeleteOutlined, EditOutlined, CopyOutlined, HistoryOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import Layout from './Layout';
import { formatPercent } from '../utils/formatPercent';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const TASK_STATUS_OPTIONS = [
    { value: 'new', label: 'Новая' },
    { value: 'in_progress', label: 'В работе' },
    { value: 'filled', label: 'Заполнена' },
    { value: 'rework', label: 'На доработке' },
    { value: 'approval', label: 'Согласование' },
    { value: 'analysis', label: 'Требует анализа' },
    { value: 'approved', label: 'Утверждено' }
];

const INITIAL_API_FILTERS = {
    onlyStatus: [], stage: [], assigned_user_fio: [], status_invoices: [],
    region: [], object_id: [], filling_user: [], news_type_name: [],
    geCreated: null, leCreated: null, geExit: null, leExit: null, geUpdated: null, leUpdated: null,
    minProfitability: null, maxProfitability: null
};

const RegistryCreatePage = () => {
    const [modalForm] = Form.useForm();
    const [currentStep, setCurrentStep] = useState(0);

    const [registries, setRegistries] = useState([]);
    const [counterparties, setCounterparties] = useState([]);
    const [availableOrders, setAvailableOrders] = useState([]);
    const [tasksSource, setTasksSource] = useState([]);
    const [invoicesSource, setInvoicesSource] = useState([]);

    const [activeTab, setActiveTab] = useState('customer_order');
    const [loading, setLoading] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [commentHistory, setCommentHistory] = useState([]);
    const [registryHistory, setRegistryHistory] = useState([]);
    const [newCommentText, setNewCommentText] = useState('');
    const commentsRef = useRef(null);
    const historyRef = useRef(null);

    const scrollToBottom = (ref) => {
        if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
    };

    // Фильтры главной страницы
    const [mainSearch, setMainSearch] = useState({ text: '', ka: '', dateRange: null, status: null });

    // Состояния просмотра и редактирования
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [selectedRegistry, setSelectedRegistry] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isAddItemsModalVisible, setIsAddItemsModalVisible] = useState(false);

    const [selectedTaskKeys, setSelectedTaskKeys] = useState([]);
    const [selectedInvoiceKeys, setSelectedInvoiceKeys] = useState([]);

    const [tasksPagination, setTasksPagination] = useState({ current: 1, pageSize: 10 });
    const [invoicesPagination, setInvoicesPagination] = useState({ current: 1, pageSize: 20 });

    const [options, setOptions] = useState({
        users: [], regions: [], objects: [], types: [], fillers: [], stages: []
    });

    const [searchText, setSearchText] = useState('');
    const [apiFilters, setApiFilters] = useState(INITIAL_API_FILTERS);

    const docType = Form.useWatch('type', modalForm);
    const selectedCpId = Form.useWatch('counterparty_id', modalForm);
    const selectedParentId = Form.useWatch('parent_id', modalForm);
    const selectedCpName = useMemo(() => {
        return counterparties.find(c => c.id === selectedCpId)?.name;
    }, [selectedCpId, counterparties]);

    useEffect(() => { fetchRegistries(); fetchDictionaries(); }, [activeTab]);

    const fetchDictionaries = async () => {
        const token = localStorage.getItem('token');
        const h = { 'Authorization': `Bearer ${token}` };
        try {
            const [u, r, o, t, f, s, cp] = await Promise.all([
                axios.get('/api/tasks/assigned-users', { headers: h }),
                axios.get('/api/tasks/regions', { headers: h }),
                axios.get('/api/tasks/service-objects', { headers: h }),
                axios.get('/api/tasks/types', { headers: h }),
                axios.get('/api/tasks/filling-users', { headers: h }),
                axios.get('/api/tasks/statuses', { headers: h }),
                axios.get('/api/counterparties?limit=1000', { headers: h })
            ]);
            setOptions({
                users: u.data.map(v => ({ value: v, label: v })),
                regions: r.data.map(v => ({ value: v, label: v })),
                objects: o.data.map(v => ({ value: v, label: v })),
                types: t.data.map(v => ({ value: v, label: v })),
                fillers: f.data.map(v => ({ value: v.login, label: v.fio || v.login })),
                stages: s.data.map(v => ({ value: v, label: v }))
            });
            setCounterparties(cp.data.data || []);
        } catch (e) { console.error('Ошибка справочников', e); }
    };

    const fetchRegistries = async () => {
        try {
            const res = await axios.get('/api/registries', {
                params: { type: activeTab },
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            setRegistries(res.data);
        } catch (e) { console.error(e); }
    };

    const handleExport = (id, format) => {
        const token = localStorage.getItem('token');
        window.open(`/api/registries/${id}/export?format=${format}&token=${token}`, '_blank');
    };

    const handleAddComment = async () => {
        if (!newCommentText.trim()) return;
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/registries/${selectedRegistry.id}/comments`,
                { text: newCommentText },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setNewCommentText('');
            // Обновляем список комментов
            const res = await axios.get(`/api/registries/${selectedRegistry.id}/comments`, { headers: { 'Authorization': `Bearer ${token}` } });
            setCommentHistory(res.data);
            setTimeout(() => scrollToBottom(commentsRef), 100);
        } catch (e) { message.error("Ошибка отправки"); }
    };

    // Группировка строк реестра по задачам для красивого вывода
    const groupedRegistryItems = useMemo(() => {
        if (!selectedRegistry?.items) return [];
        const groups = {};
        selectedRegistry.items.forEach(item => {
            const extId = item.invoice?.task?.external_id || 'Без ID';
            if (!groups[extId]) {
                groups[extId] = {
                    task: item.invoice?.task,
                    items: [],
                    total: 0
                };
            }
            groups[extId].items.push(item);
            groups[extId].total += parseFloat(item.invoice?.amount || 0);
        });
        return Object.values(groups);
    }, [selectedRegistry]);

    // Группировка ВДР по задачам для 2-го шага мастера создания
    const groupedInvoicesSource = useMemo(() => {
        if (!invoicesSource || invoicesSource.length === 0) return [];

        // Фильтруем строки по выбранному в селекте контрагенту
        const filtered = selectedCpName
            ? invoicesSource.filter(inv => inv.counterparty === selectedCpName)
            : invoicesSource;

        const groups = {};
        filtered.forEach(inv => {
            const extId = inv.task?.external_id || 'ID?';
            if (!groups[extId]) {
                groups[extId] = { task: inv.task, items: [] };
            }
            groups[extId].items.push(inv);
        });
        return Object.values(groups);
    }, [invoicesSource, selectedCpName]);

    const filteredRegistries = useMemo(() => {
        return registries.filter(r => {
            const searchVal = mainSearch.text?.toLowerCase();
            const matchText = !searchVal ||
                r.number?.toLowerCase().includes(searchVal) ||
                r.title?.toLowerCase().includes(searchVal) ||
                String(r.id).includes(searchVal); // Добавлен поиск по внутреннему номеру

            const matchKA = !mainSearch.ka || r.counterparty_name?.toLowerCase().includes(mainSearch.ka.toLowerCase());
            const matchStatus = !mainSearch.status || r.status === mainSearch.status;
            const matchDate = !mainSearch.dateRange || (
                dayjs(r.date).isAfter(mainSearch.dateRange[0], 'day') &&
                dayjs(r.date).isBefore(mainSearch.dateRange[1], 'day')
            );
            return matchText && matchKA && matchStatus && matchDate;
        });
    }, [registries, mainSearch]);

    // УДАЛЕНИЕ СТРОКИ (Пункт 4 - РАБОТАЕТ)
    const removeItemFromRegistry = async (itemId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/registries/items/${itemId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            message.success('Строка удалена');

            // Сразу обновляем данные в модальном окне
            const res = await axios.get(`/api/registries/${selectedRegistry.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setSelectedRegistry(res.data);
            fetchRegistries(); // Обновляем основную таблицу
        } catch (e) { message.error('Ошибка при удалении'); }
    };

    const addItemsToRegistry = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/registries/${selectedRegistry.id}/add-items`,
                { invoiceIds: selectedInvoiceKeys },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            message.success('Строки добавлены');
            setIsAddItemsModalVisible(false);
            setSelectedInvoiceKeys([]);
            setSelectedTaskKeys([]);

            // Обновляем данные текущего реестра
            fetchRegistries();
            setIsViewModalVisible(false); // Закрываем просмотр, чтобы пользователь увидел обновленную сумму в таблице
        } catch (e) { message.error('Ошибка при добавлении'); }
    };

    useEffect(() => {
        const currentType = docType || activeTab;
        // Нам нужны заказы, только если мы создаем Акт
        if (currentType?.includes('act')) {
            const token = localStorage.getItem('token');
            axios.get('/api/registries/available-orders', {
                params: { type: currentType },
                headers: { 'Authorization': `Bearer ${token}` }
            }).then(res => {
                console.log("Заказы загружены:", res.data); // Добавьте для отладки в консоль
                setAvailableOrders(res.data);
            }).catch(err => console.error("Ошибка загрузки заказов:", err));
        }
    }, [docType, activeTab, currentStep]);

    useEffect(() => {
        // Если мы на 2-м шаге и пользователь поменял тип документа
        if (currentStep === 1 && selectedTaskKeys.length > 0) {
            const refreshInvoices = async () => {
                setModalLoading(true);
                setSelectedInvoiceKeys([]); // Сбрасываем выбор (Пункт 2 вашего требования)
                try {
                    const token = localStorage.getItem('token');
                    const res = await axios.get('/api/registries/get-invoices-by-tasks', {
                        params: {
                            taskIds: selectedTaskKeys.join(','),
                            docType: docType || activeTab
                        },
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    setInvoicesSource(res.data);
                } catch (e) {
                    message.error('Ошибка обновления списка строк');
                } finally {
                    setModalLoading(false);
                }
            };
            refreshInvoices();
        }
    }, [docType]); // Следим за изменением типа

    const handleFilterChange = (key, value) => setApiFilters(p => ({ ...p, [key]: value }));

    const handleSearchTasks = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const queryParams = {};
            if (searchText.trim()) {
                const res = await axios.get(`/api/tasks/${encodeURIComponent(searchText.trim())}`, { headers: { 'Authorization': `Bearer ${token}` } });
                setTasksSource(Array.isArray(res.data) ? res.data : [res.data]);
            } else {
                for (const key in apiFilters) {
                    const val = apiFilters[key];
                    if (val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) continue;
                    if (dayjs.isDayjs(val)) queryParams[key] = val.add(3, 'hour').format('YYYY-MM-DD HH:mm:ss');
                    else queryParams[key] = Array.isArray(val) ? val.join(',') : val;
                }
                const res = await axios.get('/api/tasks', { params: { ...queryParams, limit: 1000 }, headers: { 'Authorization': `Bearer ${token}` } });
                setTasksSource(res.data || []);
            }
        } finally { setLoading(false); }
    };

    const goToInvoicesStep = async () => {
        try {
            if (selectedTaskKeys.length === 0) return message.warning('Выберите хотя бы одну задачу');

            setModalLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/registries/get-invoices-by-tasks', {
                params: {
                    taskIds: selectedTaskKeys.join(','),
                    // ИСПРАВЛЕНИЕ: берем activeTab если docType еще не выбран в форме
                    docType: docType || activeTab
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setInvoicesSource(res.data);
            setCurrentStep(1);
        } catch (e) {
            message.error('Ошибка получения данных');
        } finally {
            setModalLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            const vals = await modalForm.validateFields();
            setIsSaving(true);
            const cp = counterparties.find(c => c.id === vals.counterparty_id);
            await axios.post('/api/registries', { ...vals, counterparty_name: cp?.name, selectedInvoiceIds: selectedInvoiceKeys }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            message.success('Документ создан');
            setIsModalVisible(false);
            setCurrentStep(0);
            modalForm.resetFields();
            fetchRegistries();
        } catch (e) { message.error('Проверьте заполнение полей'); } finally { setIsSaving(false); }
    };

    const directionText = useMemo(() => {
        const type = isEditMode ? selectedRegistry?.type : docType;
        return type?.includes('customer') ? "ДОХОДНЫХ строк" : "РАСХОДНЫХ строк";
    }, [docType, selectedRegistry, isEditMode]);

    const viewRegistryDetails = async (record) => {
        setSelectedRegistry(record);
        setIsViewModalVisible(true);
        const token = localStorage.getItem('token');
        try {
            const [hist, comms] = await Promise.all([
                axios.get(`/api/registries/${record.id}/history`, { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get(`/api/registries/${record.id}/comments`, { headers: { 'Authorization': `Bearer ${token}` } })
            ]);
            setRegistryHistory(hist.data);
            setCommentHistory(comms.data);
            setTimeout(() => { scrollToBottom(commentsRef); scrollToBottom(historyRef); }, 200);
        } catch (e) { console.error("Ошибка загрузки метаданных"); }
    };

    const createActOnBasis = async (order) => {
        setIsViewModalVisible(false);

        // 1. Определяем тип акта
        const actType = order.type === 'customer_order' ? 'customer_act' : 'supplier_act';

        // 2. Предзаполняем форму (важно сделать это до открытия модалки)
        modalForm.setFieldsValue({
            type: actType,
            counterparty_id: counterparties.find(c => c.name === order.counterparty_name)?.id,
            parent_id: order.id,
            title: `На основании №${order.number}`,
            date: dayjs()
        });

        // 3. Извлекаем задачи из заказа
        const taskIds = [...new Set(order.items.map(i => i.invoice.task_id))];
        setSelectedTaskKeys(taskIds);
        setSelectedInvoiceKeys([]);

        // 4. Открываем мастер и включаем загрузку
        setIsModalVisible(true);
        setModalLoading(true);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/registries/get-invoices-by-tasks', {
                params: {
                    taskIds: taskIds.join(','),
                    docType: actType
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });

            setInvoicesSource(res.data);

            // 5. Переходим сразу на 2 шаг
            setCurrentStep(1);
        } catch (e) {
            message.error('Ошибка подготовки данных на основании');
            setIsModalVisible(false);
        } finally {
            setModalLoading(false);
        }
    };

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Title level={2}>Документы</Title>
                    <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => { setIsModalVisible(true); setCurrentStep(0); setSelectedTaskKeys([]); setSelectedInvoiceKeys([]); setIsEditMode(false); }}>Создать документ</Button>
                </Row>

                <Card style={{ marginBottom: 20 }}>
                    <Row gutter={16}>
                        <Col span={6}><Input placeholder="Поиск по № или названию" prefix={<SearchOutlined />} value={mainSearch.text} onChange={e => setMainSearch(p => ({ ...p, text: e.target.value }))} /></Col>
                        <Col span={6}>

                            <Select
                                showSearch
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="Выберите контрагента"
                                optionFilterProp="label"
                                value={mainSearch.ka}
                                onChange={val => setMainSearch(p => ({ ...p, ka: val }))}
                                options={counterparties.map(c => ({
                                    label: c.name,
                                    value: c.name // Используем имя, так как поиск в filteredRegistries идет по строке
                                }))}
                            />
                        </Col>
                        <Col span={6}><RangePicker style={{ width: '100%' }} value={mainSearch.dateRange} onChange={v => setMainSearch(p => ({ ...p, dateRange: v }))} /></Col>
                        <Col span={6}><Select placeholder="Статус" style={{ width: '100%' }} allowClear value={mainSearch.status} onChange={v => setMainSearch(p => ({ ...p, status: v }))}><Select.Option value="draft">Черновик</Select.Option><Select.Option value="approved">Утвержден</Select.Option></Select></Col>
                    </Row>
                </Card>

                <Card>
                    <Select value={activeTab} onChange={setActiveTab} style={{ width: 350, marginBottom: 20 }}>
                        <Select.Option value="customer_order">Заказы покупателей</Select.Option>
                        <Select.Option value="customer_act">Акты покупателей</Select.Option>
                        <Select.Option value="supplier_order">Заказы поставщиков</Select.Option>
                        <Select.Option value="supplier_act">Акты поставщикам</Select.Option>
                    </Select>
                    <Table
                        dataSource={filteredRegistries}
                        rowKey="id"
                        columns={[
                            { title: 'Вн. №', dataIndex: 'id', width: 80 },
                            { title: 'Дата док.', dataIndex: 'date', render: d => dayjs(d).format('DD.MM.YYYY') },
                            { title: 'Номер док.', dataIndex: 'number' },
                            { title: 'Наименование', dataIndex: 'title' },
                            { title: 'Контрагент', dataIndex: 'counterparty_name' },
                            { title: 'Создан', dataIndex: 'created_at', render: d => dayjs(d).format('DD.MM.YYYY HH:mm') },
                            {
                                title: 'Создатель',
                                dataIndex: ['user', 'fio'],
                                width: 150,
                                render: (fio) => fio || 'Система'
                            },
                            { title: 'Сумма', dataIndex: 'total_amount', align: 'right', render: v => `${parseFloat(v || 0).toLocaleString()} р.` },
                            { title: 'Статус', dataIndex: 'status', render: s => <Tag color={s === 'draft' ? 'orange' : 'blue'}>{s?.toUpperCase()}</Tag> },
                            { title: 'Действия', key: 'actions', render: (_, record) => (
                                    <Space>
                                        <Button icon={<EyeOutlined />} onClick={() => { setSelectedRegistry(record); setIsViewModalVisible(true); setIsEditMode(false); viewRegistryDetails(record); }} />
                                        <Button icon={<FileExcelOutlined />} onClick={() => handleExport(record.id, 'excel')} />
                                        <Button icon={<FilePdfOutlined />} onClick={() => handleExport(record.id, 'pdf')} />
                                    </Space>
                                )}
                        ]}
                    />
                </Card>

                <Modal
                    title={isEditMode ? `Редактирование №${selectedRegistry?.number}` : `Просмотр №${selectedRegistry?.number}`}
                    open={isViewModalVisible}
                    onCancel={() => setIsViewModalVisible(false)}
                    width={1200}
                    footer={[
                        selectedRegistry?.type.includes('order') && (
                            <Button key="basis" type="primary" icon={<CopyOutlined />} onClick={() => createActOnBasis(selectedRegistry)}>
                                Создать Акт на основании
                            </Button>
                        ),
                        <Button key="close" onClick={() => setIsViewModalVisible(false)}>Закрыть</Button>,
                        !isEditMode && <Button key="edit" type="primary" icon={<EditOutlined />} onClick={() => setIsEditMode(true)}>Режим редактирования</Button>,
                        isEditMode && (
                            <Button
                                key="add"
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setIsModalVisible(true); // Открываем основной мастер
                                    setCurrentStep(0);
                                    setSelectedTaskKeys([]);
                                    setSelectedInvoiceKeys([]);

                                    // ПРЕДЗАПОЛНЕНИЕ: устанавливаем тип и контрагента из открытого документа
                                    modalForm.setFieldsValue({
                                        type: selectedRegistry.type,
                                        counterparty_id: counterparties.find(c => c.name === selectedRegistry.counterparty_name)?.id,
                                        title: selectedRegistry.title,
                                        number: selectedRegistry.number,
                                        date: dayjs(selectedRegistry.date)
                                    });
                                }}
                            >
                                Добавить строки
                            </Button>
                        )
                    ]}
                >
                    {selectedRegistry && (
                        <div>
                            <Row gutter={24} style={{ marginBottom: 20 }}>
                                <Col span={8}><Paragraph><strong>Контрагент:</strong> {selectedRegistry.counterparty_name}</Paragraph></Col>
                                <Col span={8}><Paragraph><strong>Дата документа:</strong> {dayjs(selectedRegistry.date).format('DD.MM.YYYY')}</Paragraph></Col>
                                <Col span={8}><Paragraph><strong>Итого по документу:</strong> <Text type="danger" strong style={{fontSize: '18px'}}>{parseFloat(selectedRegistry.total_amount).toLocaleString()} р.</Text></Paragraph></Col>
                            </Row>

                            <Divider orientation="left">Состав документа (по задачам)</Divider>

                            {groupedRegistryItems.map((group, gIdx) => (
                                <div key={gIdx} style={{ marginBottom: 25, border: '1px solid #f0f0f0', borderRadius: '8px', overflow: 'hidden' }}>
                                    {/* Шапка группы задачи */}
                                    <div style={{ background: '#fafafa', padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                        <Space size="large">
                                            <Tag color="blue" style={{ fontSize: '14px', padding: '2px 10px' }}>#{group.task?.external_id}</Tag>
                                            <Text strong>{group.task?.title}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>({group.task?.news_type_name})</Text>
                                        </Space>
                                        <Text strong>Сумма по задаче: <span style={{ color: '#1890ff' }}>{group.total.toLocaleString()} р.</span></Text>
                                    </div>

                                    {/* Таблица со строками внутри конкретной задачи */}
                                    <Table
                                        rowKey="id"
                                        size="small"
                                        dataSource={group.items}
                                        pagination={false}
                                        columns={[
                                            {
                                                title: 'Наименование / Комментарий',
                                                key: 'work',
                                                render: (_, r) => (
                                                    <div>
                                                        <div>{r.invoice?.rate_name}</div>
                                                        {r.invoice?.note && <div style={{fontStyle:'italic', color:'orange', fontSize:'11px'}}>КП: {r.invoice.note}</div>}
                                                    </div>
                                                )
                                            },
                                            { title: 'Кол-во', dataIndex: ['invoice', 'quantity'], align: 'center', width: 80 },
                                            { title: 'Цена', dataIndex: ['invoice', 'price'], align: 'right', width: 120, render: v => `${parseFloat(v || 0).toLocaleString()} р.` },
                                            { title: '%', dataIndex: ['invoice', 'percent'], align: 'center', width: 60, render: v => `${formatPercent(v)}%` },
                                            {
                                                title: 'Штраф',
                                                dataIndex: ['invoice', 'penalty'],
                                                align: 'center',
                                                width: 100,
                                                render: v => v > 0 ? <Tag color="error">-{v}%</Tag> : '-'
                                            },
                                            {
                                                title: 'Итого строка',
                                                dataIndex: ['invoice', 'amount'],
                                                align: 'right',
                                                width: 150,
                                                render: v => <b>{parseFloat(v || 0).toLocaleString()} р.</b>
                                            },
                                            isEditMode && {
                                                title: '',
                                                key: 'del',
                                                width: 50,
                                                render: (_, r) => (
                                                    <Popconfirm title="Удалить строку?" onConfirm={() => removeItemFromRegistry(r.id)}>
                                                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                                                    </Popconfirm>
                                                )
                                            }
                                        ].filter(Boolean)}
                                    />
                                </div>
                            ))}
                            {/* БЛОК ИСТОРИИ И КОММЕНТАРИЕВ */}
                            <Divider />
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Title level={5}>Комментарии</Title>
                                    <div ref={commentsRef} style={{ height: 200, overflowY: 'auto', background: '#f9f9f9', padding: 10, borderRadius: 4, border: '1px solid #eee', marginBottom: 10 }}>
                                        {commentHistory.length > 0 ? commentHistory.map((c, i) => (
                                            <div key={i} style={{ marginBottom: 8, fontSize: 12 }}>
                                                <Text type="secondary" style={{fontSize: 10}}>{dayjs(c.created_at).format('DD.MM HH:mm')}</Text> <br/>
                                                <b>{c.user?.fio || 'Пользователь'}:</b> {c.text}
                                            </div>
                                        )) : <Text type="secondary">Комментариев пока нет</Text>}
                                    </div>
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Input
                                            placeholder="Напишите комментарий..."
                                            value={newCommentText}
                                            onChange={e => setNewCommentText(e.target.value)}
                                            onPressEnter={handleAddComment}
                                        />
                                        <Button type="primary" onClick={handleAddComment}>Отправить</Button>
                                    </Space.Compact>
                                </Col>
                                <Col span={12}>
                                    <Title level={5}><HistoryOutlined /> История изменений</Title>
                                    <div ref={historyRef} style={{ height: 250, overflowY: 'auto', background: '#fff', padding: 10, borderRadius: 4, border: '1px solid #eee' }}>
                                        {registryHistory.length > 0 ? registryHistory.map((h, i) => (
                                            <div key={i} style={{ marginBottom: 8, fontSize: 12 }}>
                                                <Text type="secondary" style={{fontSize: 10}}>{dayjs(h.created_at).format('DD.MM HH:mm')}</Text> <br/>
                                                <b>{h.user?.fio || 'Система'}:</b> {h.action_text}
                                            </div>
                                        )) : <Text type="secondary">История пуста</Text>}
                                    </div>
                                </Col>
                            </Row>

                        </div>
                    )}
                </Modal>

                <Modal title="Мастер формирования документа" open={isModalVisible} onCancel={() => setIsModalVisible(false)} width="98%" footer={null} destroyOnClose>
                    <Form form={modalForm} layout="vertical" preserve={true}>
                        <Steps current={currentStep} items={[{ title: 'Поиск заданий' }, { title: 'Выбор позиций' }]} style={{ marginBottom: 25 }} />
                        <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>


                            {/* ФИЛЬТРЫ ЗАДАЧ (ВОССТАНОВЛЕНЫ ПОЛНОСТЬЮ) */}
                            <div style={{ background: '#f5f5f5', padding: '15px 20px', borderRadius: 8, marginBottom: 20 }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={4}><Text type="secondary">Внешний ID:</Text><Input placeholder="ID" value={searchText} onChange={e => setSearchText(e.target.value)} /></Col>
                                    <Col span={4}><Text type="secondary">Объект (БС):</Text><Select mode="multiple" style={{width:'100%'}} options={options.objects} value={apiFilters.object_id} onChange={v => handleFilterChange('object_id', v)} showSearch maxTagCount="responsive" /></Col>
                                    <Col span={4}><Text type="secondary">Заказчик (Регион):</Text><Select mode="multiple" style={{width:'100%'}} options={options.regions} value={apiFilters.region} onChange={v => handleFilterChange('region', v)} showSearch /></Col>
                                    <Col span={4}><Text type="secondary">Вид работ:</Text><Select mode="multiple" style={{width:'100%'}} options={options.types} value={apiFilters.news_type_name} onChange={v => handleFilterChange('news_type_name', v)} showSearch /></Col>
                                    <Col span={4}><Text type="secondary">Исполнитель:</Text><Select mode="multiple" style={{width:'100%'}} options={options.users} value={apiFilters.assigned_user_fio} onChange={v => handleFilterChange('assigned_user_fio', v)} showSearch /></Col>
                                    <Col span={4}><Text type="secondary">Заполнил ВДР:</Text><Select mode="multiple" style={{width:'100%'}} options={options.fillers} value={apiFilters.filling_user} onChange={v => handleFilterChange('filling_user', v)} showSearch /></Col>
                                </Row>
                                <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
                                    <Col span={4}><Text type="secondary">Этапы:</Text><Select mode="multiple" style={{width:'100%'}} options={options.stages} value={apiFilters.onlyStatus} onChange={v => handleFilterChange('onlyStatus', v)} /></Col>
                                    <Col span={4}><Text type="secondary">Стадия:</Text><Select mode="multiple" style={{width:'100%'}} options={[{value:1,label:'В работе'},{value:2,label:'Завершено'}]} value={apiFilters.stage} onChange={v => handleFilterChange('stage', v)} /></Col>
                                    <Col span={4}><Text type="secondary">Статус ВДР:</Text><Select mode="multiple" style={{width:'100%'}} options={TASK_STATUS_OPTIONS} value={apiFilters.status_invoices} onChange={v => handleFilterChange('status_invoices', v)} /></Col>
                                    <Col span={6}>
                                        <Text type="secondary">Рентабельность (%):</Text>
                                        <div style={{display:'flex', gap: 8}}><InputNumber placeholder="От" value={apiFilters.minProfitability} onChange={v => handleFilterChange('minProfitability', v)} /><InputNumber placeholder="До" value={apiFilters.maxProfitability} onChange={v => handleFilterChange('maxProfitability', v)} /></div>
                                    </Col>
                                    <Col span={6} style={{textAlign:'right', paddingTop: 22}}>
                                        <Space><Button type="primary" icon={<SyncOutlined />} onClick={handleSearchTasks} loading={loading}>Применить</Button><Button danger icon={<ClearOutlined />} onClick={() => {setApiFilters(INITIAL_API_FILTERS); setSearchText('');}}>Сбросить</Button></Space>
                                    </Col>
                                </Row>
                                <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
                                    <Col span={8}><Text type="secondary">Дата создания:</Text><RangePicker showTime style={{width:'100%'}} format="DD.MM.YYYY HH:mm" value={[apiFilters.geCreated, apiFilters.leCreated]} onChange={v => { handleFilterChange('geCreated', v?.[0]); handleFilterChange('leCreated', v?.[1]); }} /></Col>
                                    <Col span={8}><Text type="secondary">Покидание сайта:</Text><RangePicker showTime style={{width:'100%'}} format="DD.MM.YYYY HH:mm" value={[apiFilters.geExit, apiFilters.leExit]} onChange={v => { handleFilterChange('geExit', v?.[0]); handleFilterChange('leExit', v?.[1]); }} /></Col>
                                    <Col span={8}><Text type="secondary">Обновление:</Text><RangePicker showTime style={{width:'100%'}} format="DD.MM.YYYY HH:mm" value={[apiFilters.geUpdated, apiFilters.leUpdated]} onChange={v => { handleFilterChange('geUpdated', v?.[0]); handleFilterChange('leUpdated', v?.[1]); }} /></Col>
                                </Row>
                            </div>

                            <Table
                                rowSelection={{
                                    selectedRowKeys: selectedTaskKeys,
                                    onChange: setSelectedTaskKeys,
                                    preserveSelectedRowKeys: true,
                                    onSelectAll: (selected) => {
                                        const allKeys = selected ? tasksSource.map(item => item.id) : [];
                                        setSelectedTaskKeys(allKeys);
                                    },
                                    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
                                }}
                                dataSource={tasksSource}
                                rowKey="id"
                                loading={loading}
                                size="small"
                                scroll={{ x: 1500 }}
                                columns={[
                                    { title: 'ID', dataIndex: 'id', width: 70 },
                                    { title: 'Внешний ID', dataIndex: 'external_id', width: 110 },
                                    { title: 'Название', dataIndex: 'title', ellipsis: true },
                                    { title: 'Вид работ', dataIndex: 'news_type_name', width: 150 },
                                    { title: 'Этап', dataIndex: ['raw_data','status_name'], width: 150 },
                                    { title: 'Статус ВДР', dataIndex: 'status_invoices', width: 130, render: s => <Tag color={s === 'approved' ? 'green' : 'blue'}>{TASK_STATUS_OPTIONS.find(o=>o.value===s)?.label || s}</Tag> },
                                    { title: 'Исполнитель', dataIndex: 'assigned_user_fio', width: 180 },
                                    {
                                        title: 'Заказчик',
                                        width: 180,
                                        render: (_, record) => {
                                            try {
                                                const rawFields = record.raw_data?.custom_fields;
                                                const customFields = typeof rawFields === 'string' ? JSON.parse(rawFields) : rawFields;
                                                return customFields?.Zakazchik?.value || record.assigned_department_name || '-';
                                            } catch (e) { return record.assigned_department_name || '-'; }
                                        }
                                    },
                                    {
                                        title: 'Рент. (%)',
                                        width: 90,
                                        align: 'right',
                                        render: (_, r) => {
                                            const inc = parseFloat(r.total_income) || 0;
                                            const exp = parseFloat(r.total_expense) || 0;
                                            if (inc === 0) return '0%';
                                            const res = Math.round(((inc - exp) / inc) * 100);
                                            return <Text type={res < 20 ? 'danger' : 'success'}>{res}%</Text>;
                                        }
                                    },
                                    { title: 'Создана', dataIndex: ['raw_data','news_date'], width: 140, render: d => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '-' },
                                    {
                                        title: 'Покидание',
                                        width: 140,
                                        render: (_, record) => {
                                            try {
                                                const rawFields = record.raw_data?.custom_fields;
                                                const customFields = typeof rawFields === 'string' ? JSON.parse(rawFields) : rawFields;
                                                let val = customFields?.Vremja_pokidanija_sayta?.value;
                                                if (!val || val === 'false') return '-';
                                                if (/^\d+$/.test(val.toString())) {
                                                    const num = val.toString().length === 10 ? Number(val) * 1000 : Number(val);
                                                    return dayjs(num).add(180 - dayjs(num).utcOffset(), 'minute').format('DD.MM.YYYY HH:mm');
                                                }
                                                return dayjs(val, "DD.MM.YYYY HH:mm:ss").isValid() ? dayjs(val, "DD.MM.YYYY HH:mm:ss").format('DD.MM.YYYY HH:mm') : val.toString().substring(0, 16);
                                            } catch (e) { return '-'; }
                                        }
                                    },
                                    { title: 'Обновлена', dataIndex:  ['raw_data','update_date'], width: 140, render: d => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '-' },
                                ]}
                                pagination={{ ...tasksPagination, showSizeChanger: true, onChange: (p, s) => setTasksPagination({ current: p, pageSize: s }) }}
                            />
                            <Button type="primary" block size="large" onClick={goToInvoicesStep} disabled={selectedTaskKeys.length === 0} style={{marginTop: 20}}>Выбрать позиции ({selectedTaskKeys.length}) <ArrowRightOutlined /></Button>
                        </div>

                        {currentStep === 1 && (
                            <>
                                <div style={{ marginBottom: 20, padding: '15px', border: '1px solid #d9d9d9', borderRadius: '8px', background: '#fff' }}>
                                    <Row gutter={16}>
                                        <Col span={4}><Form.Item name="type" disabled={isEditMode} label="Тип документа" initialValue={activeTab} rules={[{required:true}]}><Select options={[{value:'customer_order', label:'Заказ покупателя'},{value:'customer_act', label:'Акт покупателя'},{value:'supplier_order', label:'Заказ поставщика'},{value:'supplier_act', label:'Акт поставщика'}]} /></Form.Item></Col>
                                        <Col span={5}><Form.Item name="title" label="Наименование"><Input placeholder="Напр: Работы за март" /></Form.Item></Col>
                                        <Col span={3}><Form.Item name="number" label="Номер документа" rules={[{required:false}]}><Input /></Form.Item></Col>
                                        <Col span={4}><Form.Item name="date" label="Дата документа" rules={[{required:true}]} initialValue={dayjs()}><DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" /></Form.Item></Col>
                                        <Col span={8}><Form.Item name="counterparty_id" disabled={isEditMode} label="Контрагент" rules={[{required:true}]}><Select showSearch options={counterparties.map(c=>({label:c.name, value:c.id}))} optionFilterProp="label" /></Form.Item></Col>
                                        {(docType || activeTab)?.includes('act') && (
                                            <Col span={4}>
                                                <Form.Item name="parent_id" label="Привязать к Заказу">
                                                    <Select
                                                        placeholder="Выберите заказ"
                                                        allowClear
                                                        showSearch
                                                        optionFilterProp="label"
                                                        options={availableOrders
                                                            .filter(o => {
                                                                if (!selectedCpName) return true; // Если КА не выбран, показываем все
                                                                // Сравниваем без учета регистра и пробелов
                                                                return o.counterparty_name?.trim().toLowerCase() === selectedCpName.trim().toLowerCase();
                                                            })
                                                            .map(o => ({
                                                                label: `Заказ №${o.number} от ${dayjs(o.date).format('DD.MM.YY')}`,
                                                                value: o.id
                                                            }))
                                                        }
                                                    />
                                                </Form.Item>
                                            </Col>
                                        )}
                                    </Row>
                                </div>

                                <Alert message={`Шаг 2: Выберите строки из ВДР для включения в документ.`} type="info" showIcon style={{marginBottom: 20}} />

                                {modalLoading ? (
                                    <div style={{ textAlign: 'center', padding: '40px' }}><SyncOutlined spin /> Загрузка строк...</div>
                                ) : (
                                    groupedInvoicesSource.map((group, idx) => (
                                        <div key={idx} style={{
                                            marginBottom: 20,
                                            border: '1px solid #f0f0f0',
                                            borderRadius: '8px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                background: '#fafafa',
                                                padding: '8px 15px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                borderBottom: '1px solid #f0f0f0'
                                            }}>
                                                <Space>
                                                    <Tag color="blue">#{group.task?.external_id}</Tag>
                                                    <Text strong>{group.task?.title}</Text>
                                                </Space>
                                            </div>
                                            <Table
                                                rowSelection={{
                                                    selectedRowKeys: selectedInvoiceKeys,
                                                    onChange: setSelectedInvoiceKeys,
                                                    preserveSelectedRowKeys: true,
                                                    // ЛОГИКА БЛОКИРОВКИ:
                                                    getCheckboxProps: (record) => {
                                                        const isAct = docType?.includes('act'); // Проверяем, создаем ли мы Акт
                                                        let isDisabled = record.is_used; // Если строка уже в другом Акте — сразу блокируем

                                                        if (isAct) {
                                                            if (!selectedParentId) {
                                                                // 1. Если Заказ в шапке НЕ выбран:
                                                                // Блокируем все строки, у которых есть привязка к любому заказу
                                                                if (record.order_id) isDisabled = true;
                                                            } else {
                                                                // 2. Если Заказ в шапке ВЫБРАН:
                                                                // Разрешаем только: строки без заказа ИЛИ строки этого конкретного заказа.
                                                                // Блокируем: строки, принадлежащие ЧУЖИМ заказам.
                                                                if (record.order_id && record.order_id !== selectedParentId) {
                                                                    isDisabled = true;
                                                                }
                                                            }
                                                        }

                                                        return {
                                                            disabled: isDisabled,
                                                            // Добавим название для отладки, если нужно
                                                            name: record.id,
                                                        };
                                                    },
                                                    onSelectAll: (selected) => {
                                                        // Чтобы "Выбрать все" не выбирало заблокированные строки
                                                        const selectableIds = groupedInvoicesSource
                                                            .flatMap(g => g.items)
                                                            .filter(i => {
                                                                // Повторяем ту же логику для фильтрации массива
                                                                if (i.is_used) return false;
                                                                if (docType?.includes('act')) {
                                                                    if (!selectedParentId) return !i.order_id;
                                                                    return !i.order_id || i.order_id === selectedParentId;
                                                                }
                                                                return true;
                                                            })
                                                            .map(i => i.id);

                                                        setSelectedInvoiceKeys(selected ? selectableIds : []);
                                                    },
                                                }}
                                                dataSource={group.items}
                                                rowKey="id"
                                                size="small"
                                                pagination={false}
                                                // СЕРАЯ СТРОКА:
                                                rowClassName={(record) => record.is_used ? 'row-disabled' : ''}
                                                columns={[
                                                    {title: 'Контрагент', dataIndex: 'counterparty', width: 150},
                                                    {title: 'Наименование', dataIndex: 'rate_name'},
                                                    {
                                                        title: 'Кол-во',
                                                        dataIndex: 'quantity',
                                                        align: 'center',
                                                        width: 80
                                                    },
                                                    {
                                                        title: 'Цена',
                                                        dataIndex: 'price',
                                                        align: 'right',
                                                        width: 100,
                                                        render: v => `${parseFloat(v || 0).toLocaleString()} р.`
                                                    },
                                                    {
                                                        title: '%',
                                                        dataIndex: 'percent',
                                                        align: 'center',
                                                        width: 60,
                                                        render: v => `${formatPercent(v)}%`
                                                    },
                                                    {
                                                        title: 'Штраф',
                                                        dataIndex: 'penalty',
                                                        align: 'center',
                                                        width: 80,
                                                        render: v => v > 0 ? <Tag color="error">-{v}%</Tag> : '-'
                                                    },
                                                    {
                                                        title: 'Сумма',
                                                        dataIndex: 'amount',
                                                        align: 'right',
                                                        width: 140,
                                                        render: (v, r) => (
                                                            <Tooltip title={r.is_used ? "Уже в другом реестре" : ""}>
                                                                <b style={{color: r.is_used ? '#ccc' : 'inherit'}}>
                                                                    {parseFloat(v).toLocaleString()} р.
                                                                </b>
                                                            </Tooltip>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </div>
                                    ))
                                )}

                                <div style={{textAlign: 'right', marginBottom: 20}}>
                                    <Text strong style={{fontSize: 18}}>ИТОГО В
                                        ДОКУМЕНТЕ: {invoicesSource.filter(i => selectedInvoiceKeys.includes(i.id)).reduce((s, i) => s + parseFloat(i.amount || 0), 0).toLocaleString()} р.</Text>
                                </div>

                                <Space style={{width: '100%', justifyContent: 'space-between'}}>
                                    <Button size="large" onClick={() => setCurrentStep(0)}>Назад к задачам</Button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<SaveOutlined />}
                                        onClick={isEditMode ? addItemsToRegistry : handleCreate} // Выбор функции
                                        loading={isSaving}
                                        disabled={selectedInvoiceKeys.length === 0}
                                    >
                                        {isEditMode ? 'Сохранить изменения' : 'Сформировать документ'}
                                    </Button>
                                </Space>
                            </>
                        )}
                    </Form>
                </Modal>


            </div>
            <style>{`
                .row-disabled { background-color: #fafafa; opacity: 0.6; }
            `}</style>
        </Layout>
    );
};

export default RegistryCreatePage;